import { default as React } from 'react';
import { useEffect } from 'react';

import { AlertDisplay as DefaultAlertDisplay } from '@backstage/core-components';

/**
 * Custom AlertDisplay that suppresses transient scaffolder network errors
 *
 * This component wraps the default AlertDisplay and uses a MutationObserver
 * to hide error alerts that are network-related and don't require user action.
 * This prevents scary red "Network Error" banners during microapp deployments.
 */
export const ResilientAlertDisplay = () => {
  useEffect(() => {
    // Function to check if we're currently on a scaffolder task execution page
    const isScaffolderTaskPage = (): boolean => {
      const currentPath = window.location.pathname;
      // Only apply suppression on scaffolder task pages
      // Examples: /create/...task-details, /scaffolder/tasks, etc.
      const isOnScaffolderPage =
        currentPath.includes('/create') ||
        currentPath.includes('/scaffolder') ||
        currentPath.includes('/task');

      // Also check if there's a task progress indicator visible
      const hasTaskProgress =
        document.querySelector('[role="progressbar"]') !== null;

      return isOnScaffolderPage && hasTaskProgress;
    };

    // Function to check if an element contains a scaffolder network error
    const isScaffolderNetworkError = (element: HTMLElement): boolean => {
      // Never suppress the intentional deployment-failure alert (IDP-996). It is
      // rendered into a dedicated portal host by CustomTaskPage and is a
      // deliberate, actionable alert — not a transient network error. Without
      // this guard the heuristic below false-positives on its copy (which
      // legitimately contains the words "error" and "task"), hiding the alert.
      if (element.closest('[data-testid="deployment-failure-alert-host"]')) {
        return false;
      }

      const alertContent = element.textContent || '';
      const lowerContent = alertContent.toLowerCase();

      // Match ONLY specific transient network / log-streaming failure signals.
      // The previous generic clause (`error` + `task`/`scaffolder`) was far too
      // broad and suppressed legitimate deployment-failure alerts whose copy
      // happens to mention "task" — see IDP-996.
      //
      // IDP-1100: expanded to cover additional browser / SSE error messages:
      //   - "Load failed" (Safari fetch error)
      //   - "We cannot connect at the moment" (Backstage's own retry message
      //     from useTaskEventStream when the SSE stream drops)
      //   - ERR_CONNECTION_* / ERR_NETWORK_* (Chrome net errors)
      //   - Connection-level errors (ECONNREFUSED, ECONNRESET, ETIMEDOUT)
      //   - "socket hang up" (Node.js connection drop)
      return (
        alertContent.includes('eventstream') ||
        alertContent.includes('streamLogs') ||
        alertContent.includes('Failed to fetch') ||
        alertContent.includes('Network request failed') ||
        alertContent.includes('NetworkError') ||
        alertContent.includes('ERR_QUIC_PROTOCOL_ERROR') ||
        alertContent.includes('net::ERR') ||
        alertContent.includes('AbortError') ||
        lowerContent.includes('typeerror: network error') ||
        lowerContent.includes('network error') ||
        lowerContent.includes('load failed') ||
        lowerContent.includes('we cannot connect at the moment') ||
        lowerContent.includes('trying again in some seconds') ||
        lowerContent.includes('err_connection') ||
        lowerContent.includes('err_network') ||
        lowerContent.includes('econnrefused') ||
        lowerContent.includes('econnreset') ||
        lowerContent.includes('etimedout') ||
        lowerContent.includes('socket hang up') ||
        lowerContent.includes('BodyStreamBuffer')
      );
    };

    // Returns true when `element` is Backstage's raw default error alert that
    // DUPLICATES our deliberate DeploymentFailureAlert (IDP-996).
    //
    // On a failed scaffolder task Backstage renders its own error banner (the
    // collapsible "Error: …" MUI Alert above the stepper) in addition to our
    // custom alert. We replace that raw banner with a clearer, actionable alert,
    // so the default one is redundant — hide it, but ONLY while our replacement
    // is actually mounted. When our alert is absent we leave Backstage's default
    // error handling completely intact.
    const isDuplicateDeploymentError = (element: HTMLElement): boolean => {
      // Never suppress our own deliberate alert (it lives inside the portal host).
      if (element.closest('[data-testid="deployment-failure-alert-host"]')) {
        return false;
      }

      // Only suppress the default banner when our replacement alert is mounted.
      const ourAlertMounted = document.querySelector(
        '[data-testid="deployment-failure-alert-host"]',
      );
      if (!ourAlertMounted) {
        return false;
      }

      const className =
        typeof element.className === 'string' ? element.className : '';

      // The duplicate banner is Backstage's ErrorPanel, rendered via WarningPanel
      // as a styled MUI v4 Accordion with role="alert" — NOT a MuiAlert.
      // OngoingTask mounts it from `taskStream.error` as the "Error: <message>"
      // banner that duplicates our DeploymentFailureAlert (IDP-996).
      //
      // CRITICAL: the deployed build minifies JSS class names. The live banner
      // class is e.g. "MuiPaper-root-1871 MuiAccordion-root-1869 jss4-1860" — the
      // makeStyles name ("BackstageWarningPanel") is stripped, so keying on it
      // (or on MuiAlert-*Error, which never existed here) silently fails in prod.
      // The minification-safe signal is structural: the element itself is a MUI
      // Accordion with role="alert". The log stream and stepper are not
      // role="alert" accordions, so this stays narrowly targeted to the banner.
      const isAccordionAlert =
        element.getAttribute('role') === 'alert' &&
        /MuiAccordion-root/.test(className);

      // Belt-and-suspenders for non-minified builds (yarn dev / unit tests) and
      // any surface that renders the banner as a plain error-severity MUI Alert.
      const isWarningPanel =
        /BackstageWarningPanel/.test(className) ||
        element.querySelector('[class*="BackstageWarningPanel"]') !== null;
      const isErrorVariant = /MuiAlert-(standard|outlined|filled)Error/.test(
        className,
      );
      const containsErrorAlert =
        element.querySelector(
          '[class*="MuiAlert-standardError"],[class*="MuiAlert-outlinedError"],[class*="MuiAlert-filledError"]',
        ) !== null;

      return (
        isAccordionAlert ||
        isWarningPanel ||
        isErrorVariant ||
        containsErrorAlert
      );
    };

    // Combined predicate: suppress transient network errors AND the duplicate
    // default deployment-failure banner.
    const shouldSuppressAlert = (element: HTMLElement): boolean =>
      isScaffolderNetworkError(element) || isDuplicateDeploymentError(element);

    // Function to suppress/hide an error alert and associated UI elements.
    //
    // `manageRunningTaskUi` controls the side effects that only make sense while
    // a task is STILL RUNNING (transient network error): hiding the red
    // determinate progress bar and disabling "Start Over". For a real, terminal
    // deployment failure (the duplicate banner case) we only hide the redundant
    // banner and leave "Start Over" enabled so the user can restart.
    const suppressAlert = (
      element: HTMLElement,
      manageRunningTaskUi: boolean = true,
    ) => {
      const alertContent = element.textContent || '';
      // eslint-disable-next-line no-console
      console.log(
        '🔇 SUPPRESSING ALERT (Scaffolder task only):',
        alertContent.substring(0, 200),
      );

      // eslint-disable-next-line no-console
      console.log('   Element classes:', element.className);

      // eslint-disable-next-line no-console
      console.log('   Element role:', element.getAttribute('role'));

      // ONLY HIDE, DON'T REMOVE - React manages the DOM
      element.style.display = 'none';
      element.style.visibility = 'hidden';
      element.style.opacity = '0';
      element.style.height = '0';
      element.style.maxHeight = '0';
      element.style.overflow = 'hidden';
      element.setAttribute('aria-hidden', 'true');

      // Mark it as suppressed so we don't process it again
      element.setAttribute('data-suppressed', 'true');

      // Side effects below only apply while the task is still running (transient
      // network error). Skip them for terminal deployment failures so the user
      // keeps a working "Start Over" button.
      if (manageRunningTaskUi) {
        // ALWAYS KEEP the first progress bar (animated, shows task is running)
        // ONLY HIDE the second progress bar (red 100% determinate error bar)
        const allProgressBars = document.querySelectorAll(
          '.MuiLinearProgress-root',
        );
        allProgressBars.forEach((bar, index) => {
          const progressBar = bar as HTMLElement;
          // Hide determinate progress bars (aria-valuenow="100") - the red error bars
          if (progressBar.getAttribute('aria-valuenow') === '100') {
            progressBar.style.display = 'none !important';
            progressBar.style.visibility = 'hidden !important';
            progressBar.style.opacity = '0 !important';
            progressBar.style.height = '0 !important';
            progressBar.style.maxHeight = '0 !important';
            // eslint-disable-next-line no-console
            console.log(
              `   ✓ Red error progress bar ${index} hidden (determinate 100%)`,
            );
          }
        });

        // Disable the "Start Over" button while process is still running
        const startOverButton = document.querySelector('button[type="button"]');
        if (startOverButton) {
          const buttonElement = startOverButton as HTMLButtonElement;
          // Only disable if button contains "Start Over" or similar retry text
          if (
            buttonElement.textContent &&
            (buttonElement.textContent.toLowerCase().includes('start') ||
              buttonElement.textContent.toLowerCase().includes('retry') ||
              buttonElement.textContent.toLowerCase().includes('restart'))
          ) {
            buttonElement.disabled = true;
            buttonElement.style.opacity = '0.5';
            // eslint-disable-next-line no-console
            console.log('   ✓ Start Over button disabled');
          }
        }
      }

      // eslint-disable-next-line no-console
      console.log('   ✓ Alert hidden and UI elements managed');
    };

    // Function to restore UI when task completes successfully
    const restoreUIOnSuccess = () => {
      // Re-enable the "Start Over" button
      const startOverButton = document.querySelector('button[type="button"]');
      if (startOverButton) {
        const buttonElement = startOverButton as HTMLButtonElement;
        if (
          buttonElement.textContent &&
          (buttonElement.textContent.toLowerCase().includes('start') ||
            buttonElement.textContent.toLowerCase().includes('retry') ||
            buttonElement.textContent.toLowerCase().includes('restart'))
        ) {
          buttonElement.disabled = false;
          buttonElement.style.opacity = '1';
          // eslint-disable-next-line no-console
          console.log('   ✓ Start Over button re-enabled (task completed)');
        }
      }

      // No need to show progress bars again - they're only hidden when errors occur
      // The indeterminate progress bar (animated) stays visible throughout
    };

    // Check existing alerts on mount
    const checkExistingAlerts = () => {
      // Only suppress errors when on a scaffolder task page
      if (!isScaffolderTaskPage()) {
        return; // Skip suppression if not on scaffolder page
      }

      const alerts = document.querySelectorAll(
        '[role="alert"], .MuiAlert-root, .MuiSnackbar-root, .MuiAccordion-root',
      );
      alerts.forEach(alert => {
        if (
          alert instanceof HTMLElement &&
          !alert.getAttribute('data-suppressed') &&
          shouldSuppressAlert(alert)
        ) {
          suppressAlert(alert, isScaffolderNetworkError(alert));
        }
      });

      // Check if task completed (success message visible)
      const successMessage = document.querySelector('[role="alert"]');
      if (successMessage) {
        const content = successMessage.textContent || '';
        if (
          content.toLowerCase().includes('success') ||
          content.toLowerCase().includes('completed') ||
          content.toLowerCase().includes('created')
        ) {
          restoreUIOnSuccess();
        }
      }
    };

    // Run initial check
    checkExistingAlerts();

    // Set up interval to periodically check for alerts - check frequently!
    const intervalId = setInterval(checkExistingAlerts, 100); // Check every 100ms

    // Create a MutationObserver to watch for new alert elements
    const observer = new MutationObserver(mutations => {
      // Only process mutations when on a scaffolder task page
      if (!isScaffolderTaskPage()) {
        return;
      }

      mutations.forEach(mutation => {
        mutation.addedNodes.forEach(node => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            const element = node as HTMLElement;

            // Check the element itself
            if (shouldSuppressAlert(element)) {
              suppressAlert(element, isScaffolderNetworkError(element));
            }

            // Also check children with alert roles
            const childAlerts = element.querySelectorAll(
              '[role="alert"], .MuiAlert-root, .MuiSnackbar-root, .MuiAccordion-root',
            );
            childAlerts.forEach(childAlert => {
              if (
                childAlert instanceof HTMLElement &&
                shouldSuppressAlert(childAlert)
              ) {
                suppressAlert(childAlert, isScaffolderNetworkError(childAlert));
              }
            });
          }
        });
      });
    });

    // Observe the document body for changes
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class'],
    });

    // Cleanup on unmount
    return () => {
      observer.disconnect();
      clearInterval(intervalId);
    };
  }, []);

  return <DefaultAlertDisplay />;
};
